import { getRouteApi, Link, useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { usePageTitle } from "@/routes/__root";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthShell, safeRedirect } from "@/components/site/AuthShell";
import { FieldError, fieldErrors, ProblemAlert } from "@/components/site/Feedback";
import { Turnstile, useTurnstileState } from "@/components/site/Turnstile";
import { joinLabel } from "@/components/site/SiteHeader";
import { api, ApiError } from "@/lib/api";

const routeApi = getRouteApi("/login");

export function LoginPage() {
  usePageTitle("Member Login");
  const { redirect } = routeApi.useSearch();
  const router = useRouter();
  const queryClient = useQueryClient();
  const turnstile = useTurnstileState();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const login = useMutation({
    mutationFn: () => api.auth.login(email.trim(), password, turnstile.token),
    onSuccess: () => {
      queryClient.clear();
      router.history.push(safeRedirect(redirect));
    },
    onError: () => turnstile.reset(),
  });

  const notConfirmed = login.error instanceof ApiError && login.error.errorKey === "EmailNotConfirmed";

  return (
    <AuthShell
      eyebrow="Members' portal"
      title="Welcome back"
      subtitle="Sign in to reach your dashboard, video library and the AI Deal Analyzer."
    >
      <form
        className="space-y-5"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          if (!turnstile.missing) login.mutate();
        }}
      >
        <ProblemAlert error={login.error && !fieldErrors(login.error, "Email") ? login.error : null} />
        {notConfirmed && (
          <p className="text-sm text-muted-foreground">
            Didn't get the email?{" "}
            <Link to="/resend-confirmation" search={{ email: email.trim() || undefined }} className="font-medium text-foreground underline underline-offset-4">
              Send the confirmation link again
            </Link>
          </p>
        )}
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" autoComplete="email" required placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
          <FieldError messages={fieldErrors(login.error, "Email")} />
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <Link to="/forgot-password" className="text-xs text-muted-foreground underline-offset-4 hover:underline">
              Forgot password?
            </Link>
          </div>
          <Input id="password" type="password" autoComplete="current-password" required placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} />
          <FieldError messages={fieldErrors(login.error, "Password")} />
        </div>
        <Turnstile ref={turnstile.ref} onToken={turnstile.setToken} onEnabled={turnstile.setEnabled} />
        <Button type="submit" variant="gold" size="lg" className="w-full" disabled={login.isPending || turnstile.missing || !email || !password}>
          {login.isPending && <Loader2 className="animate-spin" />}
          Sign in
        </Button>
      </form>

      <p className="mt-6 text-sm text-muted-foreground">
        No account yet?{" "}
        <Link to="/signup" search={{ redirect }} className="font-medium text-foreground underline underline-offset-4">
          {joinLabel}
        </Link>
      </p>
    </AuthShell>
  );
}
